import { useEffect, useState } from "react";
import axios from "axios";
import { Package, Plus, Trash2, Save, CheckCircle2 } from "lucide-react";
import { API_BASE } from "../../lib/site";

const CATS = ["Alimentos", "Bebidas", "Bebidas 18+", "Higiene", "Limpeza", "Conveniência"];
const fmtBRL = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#1A231E] focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function AdminEstoque() {
    const [produtos, setProdutos] = useState([]);
    const [msg, setMsg] = useState(null);
    const [form, setForm] = useState({ nome: "", categoria: CATS[0], preco: "", estoque: 0, restrito_18: false });
    const [editEstoque, setEditEstoque] = useState({});

    const load = () =>
        axios.get(`${API_BASE}/produtos/todos`, { withCredentials: true }).then(({ data }) => setProdutos(data)).catch(() => {});

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        setMsg(null);
        try {
            await axios.post(
                `${API_BASE}/produtos`,
                { ...form, preco: parseFloat(form.preco) || 0, estoque: parseInt(form.estoque) || 0 },
                { withCredentials: true }
            );
            setForm({ nome: "", categoria: CATS[0], preco: "", estoque: 0, restrito_18: false });
            setMsg({ ok: true, text: "Produto cadastrado!" });
            load();
        } catch (err) {
            setMsg({ ok: false, text: err.response?.data?.detail || "Erro ao cadastrar" });
        }
    };

    const salvar = async (p) => {
        const estoque = parseInt(editEstoque[p.id] ?? p.estoque);
        try {
            await axios.put(`${API_BASE}/produtos/${p.id}`, { ...p, estoque }, { withCredentials: true });
            setMsg({ ok: true, text: `Estoque de "${p.nome}" atualizado!` });
            load();
        } catch (err) {
            setMsg({ ok: false, text: err.response?.data?.detail || "Erro ao atualizar" });
        }
    };

    const toggleAtivo = async (p) => {
        await axios.put(`${API_BASE}/produtos/${p.id}`, { ...p, ativo: !p.ativo }, { withCredentials: true });
        load();
    };

    const excluir = async (p) => {
        await axios.delete(`${API_BASE}/produtos/${p.id}`, { withCredentials: true });
        load();
    };

    return (
        <section id="admin-estoque" data-testid="admin-estoque-section" className="scroll-mt-28">
            <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-[#0E6B3F]" />
                <h2 className="text-2xl font-extrabold">Controle de estoque</h2>
            </div>
            <p className="mt-2 text-sm text-[#1A231E]/60 font-semibold">
                Cadastre produtos e ajuste quantidades. O app do cliente mostra tudo em tempo real.
            </p>

            <form onSubmit={create} data-testid="admin-produto-form" className="mt-5 rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft p-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3.5">
                <input data-testid="admin-produto-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome do produto" className={`${inputCls} lg:col-span-2`} />
                <select data-testid="admin-produto-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className={inputCls}>
                    {CATS.map((c) => (
                        <option key={c}>{c}</option>
                    ))}
                </select>
                <input data-testid="admin-produto-preco" required type="number" step="0.01" min="0" value={form.preco} onChange={(e) => setForm({ ...form, preco: e.target.value })} placeholder="Preço (R$)" className={inputCls} />
                <input data-testid="admin-produto-estoque" required type="number" min="0" value={form.estoque} onChange={(e) => setForm({ ...form, estoque: e.target.value })} placeholder="Qtd." className={inputCls} />
                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-[#1A231E]/60">
                        <input type="checkbox" data-testid="admin-produto-18" checked={form.restrito_18} onChange={(e) => setForm({ ...form, restrito_18: e.target.checked })} className="w-4 h-4 accent-[#F57A3A]" />
                        18+
                    </label>
                    <button type="submit" data-testid="admin-produto-submit" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#F57A3A] hover:bg-[#E06324] text-[#F7F4EC] font-extrabold py-2.5 text-sm transition-colors">
                        <Plus className="w-4 h-4" />
                        Adicionar
                    </button>
                </div>
            </form>

            {msg && (
                <p data-testid="admin-estoque-message" className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${msg.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"}`}>
                    {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                    {msg.text}
                </p>
            )}

            <div className="mt-5 rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] font-extrabold uppercase tracking-wider text-[#1A231E]/45 border-b-2 border-[#1A231E]/10">
                            <th className="px-4 py-3">Produto</th>
                            <th className="px-4 py-3">Preço</th>
                            <th className="px-4 py-3">Estoque</th>
                            <th className="px-4 py-3">Ativo</th>
                            <th className="px-4 py-3 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {produtos.map((p) => (
                            <tr key={p.id} data-testid="admin-estoque-item" className="border-b border-[#1A231E]/5 last:border-0">
                                <td className="px-4 py-3 font-bold">
                                    {p.nome}
                                    {p.restrito_18 && <span className="ml-2 text-[10px] font-extrabold bg-[#F57A3A] text-white px-1.5 py-0.5 rounded-full">18+</span>}
                                    <span className="block text-xs font-bold text-[#1A231E]/40">{p.categoria}</span>
                                </td>
                                <td className="px-4 py-3 font-bold text-[#0E6B3F]">{fmtBRL(p.preco)}</td>
                                <td className="px-4 py-3">
                                    <input
                                        type="number"
                                        min="0"
                                        data-testid={`admin-estoque-input-${p.id}`}
                                        value={editEstoque[p.id] ?? p.estoque}
                                        onChange={(e) => setEditEstoque({ ...editEstoque, [p.id]: e.target.value })}
                                        className="w-20 rounded-lg border-2 border-[#1A231E]/12 px-2 py-1.5 text-sm font-bold focus:outline-none focus:border-[#F57A3A]"
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <button onClick={() => toggleAtivo(p)} data-testid={`admin-ativo-${p.id}`} className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full ${p.ativo ? "bg-[#0E6B3F]/10 text-[#0E6B3F]" : "bg-red-100 text-red-500"}`}>
                                        {p.ativo ? "Ativo" : "Inativo"}
                                    </button>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-1.5">
                                        <button onClick={() => salvar(p)} data-testid={`admin-estoque-save-${p.id}`} aria-label={`Salvar ${p.nome}`} className="w-9 h-9 rounded-lg grid place-items-center text-[#0E6B3F] hover:bg-[#0E6B3F]/10 transition-colors">
                                            <Save className="w-4.5 w-4 h-4" />
                                        </button>
                                        <button onClick={() => excluir(p)} data-testid={`admin-estoque-delete-${p.id}`} aria-label={`Excluir ${p.nome}`} className="w-9 h-9 rounded-lg grid place-items-center text-red-500 hover:bg-red-50 transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {produtos.length === 0 && (
                    <p className="px-6 py-8 text-center text-sm font-semibold text-[#1A231E]/50">Nenhum produto cadastrado.</p>
                )}
            </div>
        </section>
    );
}
